import { useState, useEffect } from 'react';
import { authService } from '../services/authService';

interface LoginCredentials {
    login: string;
    password: string;
    lojaId: number;
    empresa?: string;
}

interface User {
    id: number;
    login: string;
    nome: string;
    lojaId: number;
}

export const useAuth = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(
        authService.isAuthenticated()
    );
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Sincronizar estado de autenticação entre abas do mesmo navegador
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            // Quando o token muda em outra aba, atualizar o estado desta aba
            if (e.key === 'auth_token') {
                const newToken = e.newValue;
                const wasAuthenticated = isAuthenticated;
                const nowAuthenticated = !!newToken;

                // Se o estado de autenticação mudou, atualizar
                if (wasAuthenticated !== nowAuthenticated) {
                    setIsAuthenticated(nowAuthenticated);
                    
                    // Se foi logout (token removido), limpar usuário
                    if (!nowAuthenticated) {
                        setUser(null);
                    } else if (newToken) {
                        // Se foi login, decodificar token para obter dados do usuário
                        try {
                            const base64Url = newToken.split('.')[1];
                            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                            const jsonPayload = decodeURIComponent(
                                window.atob(base64).split('').map(function (c) {
                                    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                                }).join('')
                            );
                            const decoded = JSON.parse(jsonPayload);
                            
                            setUser({
                                id: parseInt(decoded.usuarioId || '0'),
                                login: decoded.usuario || decoded.unique_name || '',
                                nome: decoded.usuario || '',
                                lojaId: parseInt(decoded.lojaId || '0')
                            });
                        } catch (e) {
                            console.error('Erro ao decodificar token:', e);
                        }
                    }
                }
            }
        };

        // Adicionar listener para mudanças no localStorage de outras abas
        window.addEventListener('storage', handleStorageChange);

        // Limpar listener ao desmontar
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [isAuthenticated]);

    // Carregar dados do usuário na inicialização se já estiver autenticado
    useEffect(() => {
        if (isAuthenticated && !user) {
            const token = authService.getToken();
            if (token) {
                try {
                    const base64Url = token.split('.')[1];
                    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                    const jsonPayload = decodeURIComponent(
                        window.atob(base64).split('').map(function (c) {
                            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                        }).join('')
                    );
                    const decoded = JSON.parse(jsonPayload);
                    
                    setUser({
                        id: parseInt(decoded.usuarioId || '0'),
                        login: decoded.usuario || decoded.unique_name || '',
                        nome: decoded.usuario || '',
                        lojaId: parseInt(decoded.lojaId || '0')
                    });
                } catch (e) {
                    console.error('Erro ao decodificar token:', e);
                }
            }
        }
    }, [isAuthenticated, user]);

    const login = async (credentials: LoginCredentials) => {
        setLoading(true);
        setError(null);

        try {
            const response = await authService.login(credentials);
            setIsAuthenticated(true);
            setUser(response.usuario);
            return response;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao fazer login';
            setError(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const logout = () => {
        authService.logout();
        setIsAuthenticated(false);
        setUser(null);
    };

    return {
        isAuthenticated,
        user,
        loading,
        error,
        login,
        logout,
    };
};
